import { mapState, mapGetters } from 'vuex'
import helper from '@/lib/helper.js'
import IntroHint from '@/lib/intro.js'
import introJsData from '@/mixins/introjs-data'
import { currentUser } from '@/lib/user.js'

export default {

    mixins: [introJsData],

    data() {
        return {
            introHint: new IntroHint(),
            swatches: [
                ['', '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3'],
                [
                    '#980000',
                    '#ff0000',
                    '#ff9900',
                    '#ffff00',
                    '#00ff00',
                    '#00ffff',
                    '#4a86e8',
                    '#0000ff',
                    '#9900ff',
                    '#ff00ff'
                ],
                [
                    '#e6b8af',
                    '#f4cccc',
                    '#fce5cd',
                    '#fff2cc',
                    '#d9ead3',
                    '#d0e0e3',
                    '#c9daf8',
                    '#cfe2f3',
                    '#d9d2e9',
                    '#ead1dc'
                ],
                [
                    '#dd7e6b',
                    '#ea9999',
                    '#f9cb9c',
                    '#ffe599',
                    '#b6d7a8',
                    '#a2c4c9',
                    '#a4c2f4',
                    '#9fc5e8',
                    '#b4a7d6',
                    '#d5a6bd'
                ],
                [
                    '#cc4125',
                    '#e06666',
                    '#f6b26b',
                    '#ffd966',
                    '#93c47d',
                    '#76a5af',
                    '#6d9eeb',
                    '#6fa8dc',
                    '#8e7cc3',
                    '#c27ba0'
                ],
                [
                    '#a61c00',
                    '#cc0000',
                    '#e69138',
                    '#f1c232',
                    '#6aa84f',
                    '#45818e',
                    '#3c78d8',
                    '#3d85c6',
                    '#674ea7',
                    '#a64d79'
                ],
                [
                    '#85200c',
                    '#990000',
                    '#b45f06',
                    '#bf9000',
                    '#38761d',
                    '#134f5c',
                    '#1155cc',
                    '#0b5394',
                    '#351c75',
                    '#741b47'
                ],
                [
                    '#5b0f00',
                    '#660000',
                    '#783f04',
                    '#7f6000',
                    '#274e13',
                    '#0c343d',
                    '#1c4587',
                    '#073763',
                    '#20124d',
                    '#4c1130'
                ]
            ],
        }
    },

    watch: {
        questionType() {
            this.clearErrorValidate()
        }
    },

    computed: {
        ...mapState({
            isApprovalPendingStatus: state => {
                return (
                    typeof state.question.approval_status !== 'undefined' &&
                    state.question.approval_status === 'pending' &&
                    state.question.approval_status != ''
                )
            },
            canEdit: state => state.question.share_setting.can_edit || false,
            userId: state => state.question.user_id,
            enableToEditQuiz: state => state.enableToEditQuiz
        }),

        ...mapGetters({
            orderAbility: 'orderAbility'
        }),

        isPaidUser() {
            return this.orderAbility.is_24hrs_access || this.orderAbility.has_subscribed
        },

        owner() {
            return currentUser().id === this.userId
        },

        canEditQuiz() {
            if (this.canEdit) {
                return this.enableToEditQuiz && !this.isApprovalPendingStatus
            }

            return this.owner && !this.isApprovalPendingStatus
        }
    },

    methods: {
        removeThemeImage() {
            if (!this.canEditQuiz) return

            const themes = this.$store.getters.themes
            const themeName = this.$store.getters.themeName

            themes[themeName].background_image = ''
            themes[themeName].background_image_source = ''
            themes[themeName].background_image_original = ''
            themes[themeName].background_image_coordinates = {}

            this.$store.commit('setThemes', themes)
        },

        removeQuizImage() {
            if (!this.canEditQuiz) return

            this.$store.commit('updateQuizImage', null)
            this.$store.commit('updateQuizOriginalImage', null)
            this.$store.commit('updateExternalQuizImageSource', null)
            this.$store.commit('updateQuizCoordinates', {})
        },

        removeBackgroundImage() {
            if (!this.canEditQuiz) return

            this.$store.commit('updateBackgroundImage', null)
            this.$store.commit('updateQuizOriginalBackgroundImage', null)
            this.$store.commit('updateExternalBackgroundImage', null)
            this.$store.commit('updateQuizBackgroundCoordinates', {})
        },

        removeQuestionImage() {
            this.callHints()

            if (!this.canEditQuiz) return

            this.$store.commit('removeQuestionImage', this.questionIndex)
        },

        removeQuestionVideo() {
            this.callHints()

            if (!this.canEditQuiz) return

            this.$store.commit('removeQuestionVideo', this.questionIndex)
        },

        removeAnswerImage(answerIndex) {
            this.callHints()

            if (!this.canEditQuiz) return

            this.$store.commit('removeAnswerImage', { questionIndex: this.questionIndex, answerIndex })
        },

        openUploadMedia(questionIndex, answerIndex) {

            if (!this.isPaidUser) {

                this.$store.commit('setPaymentModalTitleDescription', {
                    title: this.$t('my_quiz_payment_model_need_premium_feature_title'),
                    description: this.$t('my_quiz_payment_model_need_premium_feature_description')
                })

                this.$store.commit('openPaymentPopup', true)
                return
            }

            if (!this.canEditQuiz) return

            const mediaObject = {
                open: true,
                location: 'answer',
                questionIndex: questionIndex,
                answerIndex: answerIndex
            }

            this.$store.commit('toggleOpenUploadMedia', mediaObject)
        },

        inputAnswer(event, answerIndex) {
            if (!this.canEditQuiz) return

            let answer = event.target.value

            if (!helper.trim(answer).length) {
                answer = ''
            }

            this.$store.commit('updateAnswerTitle', { value: answer, questionIndex: this.questionIndex, answerIndex })
        },

        addAnswer() {
            this.callHints()

            if (!this.canEditQuiz) return

            this.$store.commit('addNewAnswer', this.questionIndex)
        },

        removeAnswer(answerIndex) {
            this.callHints()

            if (!this.canEditQuiz) return

            this.$store.commit('removeAnswer', { questionIndex: this.questionIndex, answerIndex })
        },

        validateAnswers() {
            return this.$validator.validate()
        },

        clearErrorValidate() {
            this.errors.clear()
        },

        callHints() {
            if (this.questionIndex == 0) {
                this.introHint.removeHints()
                setTimeout(() => {
                    this.introHint.register(this.hintsCreateQuestion)
                }, 500)
            }
        },

        async getCacheQuiz(quiz) {
            const name = await this.getStorageName(quiz)

            if (!localStorage.getItem(name)) return

            this.$store.commit('updateQuestion', JSON.parse(localStorage.getItem(name)))
        },

        async setCacheData(quiz) {
            const name = await this.getStorageName(quiz)
            localStorage.setItem(name, JSON.stringify(quiz))
        },

        async removeCacheQuiz(quiz) {
            const name = await this.getStorageName(quiz)

            if (!localStorage.getItem(name)) return

            localStorage.removeItem(name)
        },

        getStorageName(quiz) {
            let quizId = quiz._id

            if (quizId) return quizId

            const quizType = quiz.type

            if (quizType == 'quiz') {
                quizId = 'cache-quiz'
            }

            if (quizType == 'poll') {
                quizId = 'cache-poll'
            }

            return quizId
        },

        validateToShare() {
            if (['teacher', 'teacher_plus'].includes(currentUser().role)) {
                const validateData = {
                    // difficult: true,
                    // age: true,
                    description: true,
                    subject: true,
                    grade: true,
                    image: true,
                    numberOfQuestion: true,
                    activeQuestions: true
                }

                // if (!this.difficult) {
                //     validateData.difficult = false
                // }

                // if (!this.age.length) {
                //     validateData.age = false
                // }

                const description = this.description.replace(/\s/g, '')

                if (!description) {
                    validateData.description = false
                }

                if (!this.classSubject) {
                    validateData.subject = false
                }

                if (!this.classGrade.length) {
                    validateData.grade = false
                }

                if (!this.image) {
                    validateData.image = false
                }

                if (Number(this.showNumberQuestion) < this.minimumQuestionToShare) {
                    validateData.numberOfQuestion = false
                }

                if (this.questions.filter(question => question.status).length < this.minimumQuestionToShare) {
                    validateData.activeQuestions = false
                }

                if (Object.values(validateData).filter(error => !error).length) {
                    this.$emit('open-share-validate-modal', validateData)
                    return false
                }
            }

            return true
        }
    },
}
